/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useRef } from "react";
import { getMyVibes, getVibeBadge } from "../api/vibe";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import VibeList from "../components/MyVibe/VibeList";
import Tabs from "../components/MyVibe/Tabs";
import ExpiredVibesHeader from "../components/MyVibe/ExpiredVibesHeader";
import Loader from "../components/Loader";
import CustomPageHeader from "../components/pages/CustomPageHeader";
const MyVibe = () => {
  const location = useLocation();
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("sent"); // "sent" or "received"
  const [sentVibes, setSentVibes] = useState({
    new: [],
    expired: [],
    total: 0,
  });
  const [receivedVibes, setReceivedVibes] = useState({
    new: [],
    expired: [],
    total: 0,
  });
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(null);
  const [tabKey, setTabKey] = useState(0); // force effect on tab change

  const requestInProgress = useRef(false);
  const observerRef = useRef(null);
  const loaderRef = useRef(null);

  const [badgeCounts, setBadgeCounts] = useState({
    send_vibe_count: 0,
    receive_vibe_count: 0,
  });

  useEffect(() => {
    getVibeBadge()
      .then((res) => {
        if (res?.data) {
          setBadgeCounts({
            send_vibe_count: res.data.send_vibe_count ?? 0,
            receive_vibe_count: res.data.receive_vibe_count ?? 0,
          });
        }
      })
      .catch(() => {
        setBadgeCounts({ send_vibe_count: 0, receive_vibe_count: 0 });
      });
  }, []);

  // --- De-duplication helper
  const dedupe = (list) => {
    const seen = new Set();
    return list.filter((v) => {
      if (seen.has(v.id)) return false;
      seen.add(v.id);
      return true;
    });
  };
  useEffect(() => {
    if (location.state?.activeTab) {
      setActiveTab(location.state.activeTab);
    }
  }, []);
  // --- Fetch vibes for current tab
  useEffect(() => {
    if (requestInProgress.current) return;
    requestInProgress.current = true;
    setLoading(true);
    const type = activeTab === "sent" ? 1 : 2;
    getMyVibes(type, page)
      .then((res) => {
        const vibesList = Array.isArray(res?.data?.data) ? res.data.data : [];
        const apiLastPage = Number(res?.data?.last_page || 1);
        setLastPage(apiLastPage);
        const newVibes = [];
        const expiredVibes = [];
        vibesList.forEach((vibe) => {
          let isExpired = false;
          if (vibe.date && vibe.time) {
            // Convert both to UTC and compare
            const vibeDateTime = new Date(`${vibe.date}T${vibe.time}:00Z`);
            const nowDateTime = new Date();
            isExpired = nowDateTime > vibeDateTime;
          } else if (vibe.date) {
            isExpired = new Date(vibe.date) < new Date();
          }
          if (isExpired) {
            expiredVibes.push(vibe);
          } else {
            newVibes.push(vibe);
          }
        });
        if (type === 1) {
          setSentVibes((prev) => ({
            new:
              page === 1
                ? dedupe(newVibes)
                : dedupe([...prev.new, ...newVibes]),
            expired:
              page === 1
                ? dedupe(expiredVibes)
                : dedupe([...prev.expired, ...expiredVibes]),
            // Only count upcoming vibes
            total:
              page === 1
                ? dedupe(newVibes).length
                : dedupe([...prev.new, ...newVibes]).length,
          }));
        } else {
          // Only count received vibes where user has not performed any action
          const actionableVibes = newVibes.filter(
            (vibe) => !vibe.member_status || vibe.member_status === "pending"
          );
          setReceivedVibes((prev) => ({
            new:
              page === 1
                ? dedupe(newVibes)
                : dedupe([...prev.new, ...newVibes]),
            expired:
              page === 1
                ? dedupe(expiredVibes)
                : dedupe([...prev.expired, ...expiredVibes]),
            total: page === 1 ? actionableVibes.length : actionableVibes.length,
          }));
        }
      })
      .catch((err) => {
        if (!(err?.response?.status === 429)) {
        }
      })
      .finally(() => {
        setLoading(false);
        requestInProgress.current = false;
      });
  }, [page, activeTab, tabKey]);

  useEffect(() => {
    // Fetch sent vibes (page 1)
    getMyVibes(1, 1).then((res) => {
      const vibesList = Array.isArray(res?.data?.data) ? res.data.data : [];
      const newVibes = [];
      const expiredVibes = [];
      vibesList.forEach((vibe) => {
        let isExpired = false;
        if (vibe.date && vibe.time) {
          const vibeDateTime = new Date(`${vibe.date}T${vibe.time}:00Z`);
          const nowDateTime = new Date();
          isExpired = nowDateTime > vibeDateTime;
        } else if (vibe.date) {
          isExpired = new Date(vibe.date) < new Date();
        }
        if (isExpired) {
          expiredVibes.push(vibe);
        } else {
          newVibes.push(vibe);
        }
      });
      setSentVibes({
        new: newVibes,
        expired: expiredVibes,
        total: newVibes.length,
      });
    });

    // Fetch received vibes (page 1)
    getMyVibes(2, 1).then((res) => {
      const vibesList = Array.isArray(res?.data?.data) ? res.data.data : [];
      const newVibes = [];
      const expiredVibes = [];
      vibesList.forEach((vibe) => {
        let isExpired = false;
        if (vibe.date && vibe.time) {
          const vibeDateTime = new Date(`${vibe.date}T${vibe.time}:00Z`);
          const nowDateTime = new Date();
          isExpired = nowDateTime > vibeDateTime;
        } else if (vibe.date) {
          isExpired = new Date(vibe.date) < new Date();
        }
        if (isExpired) {
          expiredVibes.push(vibe);
        } else {
          newVibes.push(vibe);
        }
      });
      setReceivedVibes({
        new: newVibes,
        expired: expiredVibes,
        total: newVibes.length,
      });
    });
  }, []);

  // --- Reset pagination when tab changes
  // useEffect(() => {
  //   setPage(1);
  //   setLastPage(null);
  //   setSentVibes({ new: [], expired: [] });
  //   setReceivedVibes({ new: [], expired: [] });
  //   setTabKey((k) => k + 1); // force effect to re-run
  // }, [activeTab]);

  // --- Infinite scroll observer
  useEffect(() => {
    if (!loaderRef.current) return;

    if (observerRef.current) observerRef.current.disconnect();

    observerRef.current = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        const noMorePages = lastPage !== null && page >= lastPage;

        if (
          entry.isIntersecting &&
          !loading &&
          !requestInProgress.current &&
          !noMorePages
        ) {
          setPage((prev) => prev + 1);
        }
      },
      { root: null, rootMargin: "200px", threshold: 0 }
    );

    observerRef.current.observe(loaderRef.current);

    return () => {
      if (observerRef.current) observerRef.current.disconnect();
    };
  }, [loading, lastPage, page]);

  // ...existing code...

  const currentData = activeTab === "sent" ? sentVibes : receivedVibes;

  const handleTabChange = (tab) => {
    setPage(1);
    setLastPage(null);
    setSentVibes({ new: [], expired: [] });
    setReceivedVibes({ new: [], expired: [] });
    setTabKey((k) => k + 1); // force effect to re-run
    setActiveTab(tab);
  };

  return (
    <CustomPageHeader
      // hideBack={true}
      // showPageHeader={false}
      pageContainerClass="px-2 w-full mx-w-4xl"
      title={t("my_vibes", "Vibelinks")}
    >
      {/* Tabs with Total Badge on Button */}
      <Tabs
        activeTab={activeTab}
        handleTabChange={handleTabChange}
        badgeCounts={badgeCounts}
        t={t}
      />
      {/* Content */}
      <div className="mt-6">
        {loading && page === 1 ? (
          <Loader />
        ) : !currentData.new.length && !currentData.expired.length ? (
          <div className="text-center text-gray-500 text-lg font-semibold py-10">
            {activeTab === "received"
              ? t("no_data_available")
              : t("no_data_available")}
          </div>
        ) : (
          <>
            <VibeList list={currentData.new} activeTab={activeTab} />
            {currentData.expired.length > 0 && (
              <>
                <ExpiredVibesHeader t={t} />
                <VibeList list={currentData.expired} activeTab={activeTab} />
              </>
            )}
            {loading && page > 1 && (
              <div className="text-center text-gray-500 mt-3">
                <Loader />
              </div>
            )}
            <div ref={loaderRef} className="h-1" />
          </>
        )}
      </div>
    </CustomPageHeader>
  );
};

export default MyVibe;
